import type { FaqItem } from './faq.js';

/**
 * Посадочные страницы под поисковые кластеры.
 *
 * Появились по данным Вордстата, и данные оказались неожиданными. «Коммерческое
 * предложение» — 130 тысяч запросов в месяц, но верх хвоста это «образец»,
 * «пример», «шаблон» и «запрос коммерческого предложения»: люди либо читают,
 * либо, наоборот, хотят получить КП от поставщика. Инструментальный спрос живёт
 * в узких ветках, и самая большая из них — вовсе не про КП: «нейросеть для
 * презентаций» набирают 32 тысячи раз в месяц, в восемнадцать раз чаще, чем
 * «коммерческое предложение онлайн».
 *
 * Одна страница на четыре таких запроса не встанет ни по одному: поиску нужен
 * документ, отвечающий на запрос целиком. Отсюда три страницы — и главная,
 * которая остаётся продуктовой и в борьбу за эти запросы не лезет, чтобы не
 * конкурировать со своими же.
 *
 * Страницы не переставляют слова в одном тексте. Так делают дорвеи, и Яндекс
 * снимает их пачками; да и по делу у сборки презентации, КП и лендинга разные
 * материалы на входе, разный результат и разные вопросы. Общее здесь — только
 * оболочка и блок цен.
 */
export interface ClusterStep {
  num: string;
  title: string;
  text: string;
}

export interface ClusterGain {
  title: string;
  text: string;
}

export interface Cluster {
  /** Последний сегмент адреса и имя файла в `dist`. */
  slug: string;
  /**
   * Жанр, который встаёт в поле задачи на этой странице.
   *
   * Плоской строкой, как и на главной: витрина не импортирует контракты
   * кабинета. Значение обязано совпадать с `DocumentKindSchema` — сервер
   * откажет незнакомому жанру, и расхождение всплывёт на первом же запросе.
   */
  kind: string;
  /** Короткая подпись для навигации соседних страниц. */
  navLabel: string;
  /** Штамп в верхней рамке: страница притворяется документом, как и витрина. */
  stamp: string;
  /** Заголовок финального призыва. */
  ctaTitle: string;
  title: string;
  description: string;
  keywords: string;
  eyebrow: string;
  /** Заголовок страницы. Несёт запрос целиком — по нему её и находят. */
  h1: string;
  lede: string;
  bullets: string[];
  /** Подпись под кнопкой первого экрана. */
  note: string;
  stepsTitle: string;
  steps: ClusterStep[];
  gainsTitle: string;
  gains: ClusterGain[];
  templatesTitle: string;
  templatesNote: string;
  templates: string[];
  faq: FaqItem[];
}

export const CLUSTERS: Cluster[] = [
  {
    slug: 'presentations',
    kind: 'presentation',
    navLabel: 'Презентации',
    stamp: 'ПРЕЗЕНТАЦИЯ № 2026-014',
    ctaTitle: 'Следующую презентацию пусть соберёт агент',
    title: 'Нейросеть для презентаций — сделать презентацию с ИИ | Keypi',
    description:
      'Нейросеть собирает презентацию компании из ваших материалов: брифа, прайса, записи ' +
      'созвона. Правки словами, без дизайнера. Выгрузка в PPTX и PDF, ссылка всегда ' +
      'актуальна. Русский интерфейс и поддержка, оплата в рублях.',
    keywords:
      'нейросеть для презентаций, ии для презентаций, ии для создания презентаций, ' +
      'сделать презентацию ии, презентация с помощью ии, чат gpt для презентаций, ' +
      'чат gpt для создания презентаций, презентация компании, презентация онлайн',
    eyebrow: 'НЕЙРОСЕТЬ ДЛЯ ПРЕЗЕНТАЦИЙ · НА РУССКОМ · С ВЫГРУЗКОЙ В PPTX',
    h1: 'Нейросеть для презентаций: сделать презентацию с ИИ за час',
    lede:
      'Keypi собирает презентацию компании из того, что у вас уже есть: брифа, прайса, ' +
      'записи созвона, старых слайдов. Через час — готовая презентация в вашем фирменном ' +
      'стиле, по ссылке и файлом.',
    bullets: [
      'Материалы читает сама — файлы разбирает, запись созвона расшифровывает по говорящим',
      'Правки словами: «убери третий слайд», «смету разбей на два этапа» — без ТЗ дизайнеру',
      'PPTX и PDF одной кнопкой: презентацию можно дальше править в PowerPoint',
      'Русский интерфейс, русская поддержка и оплата в рублях — без карт другой страны',
    ],
    note: 'Без карты · первая версия примерно через 15 минут',
    stepsTitle: 'Как собрать презентацию нейросетью',
    steps: [
      {
        num: 'ШАГ 01',
        title: 'Пришлите материалы',
        text:
          'Бриф, прайс, отчёт, запись созвона, старая презентация — что есть. Агент читает ' +
          'файлы и расшифровывает аудио сам, перепечатывать и пересказывать ничего не нужно.',
      },
      {
        num: 'ШАГ 02',
        title: 'Ответьте на вопросы по делу',
        text:
          'Агент спросит только то, чего не хватает, чтобы презентация убеждала: кому её ' +
          'показывают, что нужно доказать, какие цифры важны. Пропустили вопрос — решит сам ' +
          'и перечислит, что решил за вас.',
      },
      {
        num: 'ШАГ 03',
        title: 'Правьте словами или руками',
        text:
          'Суть — в чате или голосом. Текст и блоки — руками прямо в превью, бесплатно и без ' +
          'ожидания. Каждая правка сохраняется отдельной версией, откат в один клик.',
      },
      {
        num: 'ШАГ 04',
        title: 'Отправьте ссылкой или файлом',
        text:
          'Ссылка всегда показывает актуальную версию — поправили после отправки, и получатель ' +
          'уже видит новое. Нужен файл: PPTX открывается в PowerPoint, PDF идёт на печать.',
      },
    ],
    gainsTitle: 'Чем это отличается от чат-бота',
    gains: [
      {
        title: 'Свёрстанный документ, а не текст',
        text:
          'Чат-бот напишет содержание, а верстать, красить и собирать файл всё равно вам. ' +
          'Здесь на выходе готовая презентация — со структурой, оформлением и выгрузкой.',
      },
      {
        title: 'Фирменный стиль закреплён',
        text:
          'Готовую презентацию можно сохранить шаблоном организации: дальше агент собирает по ' +
          'нему, и стиль не разъезжается от документа к документу.',
      },
      {
        title: 'Правки не начинают всё заново',
        text:
          'Каждая правка — версия с откатом. Не нужно перегенерировать целиком и заново искать ' +
          'то, что уже было хорошо.',
      },
      {
        title: 'Всё на русском',
        text:
          'Интерфейс, поддержка и документы — на русском. Оплата в рублях, по счёту для ' +
          'юрлица в том числе.',
      },
    ],
    templatesTitle: 'С чего начать',
    templatesNote:
      'Два готовых шаблона презентаций в каталоге — или чистый лист, если структура уже в ' +
      'голове. Любой собранный проект становится шаблоном вашей организации.',
    templates: ['Презентация компании', 'Презентация кооператива', 'Чистый лист'],
    faq: [
      {
        q: 'Какая нейросеть делает презентацию?',
        a:
          'Keypi — не одна модель, а агент: он разбирает материалы, задаёт вопросы, собирает ' +
          'структуру и верстает документ, обращаясь к разным моделям под разные задачи. ' +
          'Выбирать модель вручную не нужно.',
      },
      {
        q: 'Чем это лучше, чем сделать презентацию в чате GPT?',
        a:
          'Чат отдаёт текст: содержание слайдов придётся переносить в PowerPoint, верстать и ' +
          'оформлять руками, и так после каждой правки. Здесь на выходе свёрстанный документ — ' +
          'со структурой, оформлением, ссылкой и выгрузкой в PPTX. Правка меняет документ, а ' +
          'не выдаёт новый текст, который снова нужно верстать.',
      },
      {
        q: 'Презентация выгружается в PowerPoint?',
        a:
          'Да, в PPTX — файл открывается в PowerPoint и правится дальше руками, если нужно. ' +
          'Той же кнопкой собирается PDF. И остаётся ссылка, которая всегда показывает ' +
          'актуальную версию.',
      },
      {
        q: 'Презентация будет на русском?',
        a:
          'Да. Интерфейс, вопросы агента, документы и поддержка — на русском языке. Оплата в ' +
          'рублях: картой или по счёту для юридического лица.',
      },
      {
        q: 'Можно сделать презентацию бесплатно?',
        a:
          'Новой организации документы для знакомства начисляются при регистрации, карта для ' +
          'этого не нужна. Дальше действует подписка: она включает пакет документов на месяц.',
      },
      {
        q: 'Можно ли использовать свой шаблон?',
        a:
          'Да. Сохраните готовый проект шаблоном организации — дальше агент собирает по нему. ' +
          'Свои шаблоны видны только вашей команде.',
      },
    ],
  },
  {
    slug: 'kp',
    kind: 'offer',
    navLabel: 'КП',
    stamp: 'КП № 2026-001',
    ctaTitle: 'Следующее КП пусть соберёт агент',
    title: 'Коммерческое предложение онлайн — составить КП нейросетью | Keypi',
    description:
      'Составить коммерческое предложение онлайн: агент собирает КП из брифа, прайса и записи ' +
      'созвона. Смета, сроки и условия — на месте. Ссылка, PDF и PPTX, видно, что клиент ' +
      'открыл. Интеграция с amoCRM.',
    keywords:
      'коммерческое предложение онлайн, составить коммерческое предложение нейросеть, ' +
      'создать кп онлайн, создать кп с помощью ии, сделать кп онлайн, кп онлайн в pdf, ' +
      'конструктор коммерческих предложений, коммерческое предложение из amocrm',
    eyebrow: 'КП ОНЛАЙН · СОБИРАЕТ НЕЙРОСЕТЬ · ССЫЛКА, PDF И PPTX',
    h1: 'Коммерческое предложение онлайн: составить КП нейросетью за час',
    lede:
      'Приложите бриф, прайс и запись созвона — агент соберёт КП со сметой, сроками и ' +
      'условиями. Через час у вас документ в фирменном стиле: по ссылке, которую не стыдно ' +
      'отправить, и файлом, если клиент просит файл.',
    bullets: [
      'Смета берётся из вашего прайса, сроки и условия — из брифа и записи созвона',
      'Ссылка всегда актуальна: поправили после отправки — клиент уже видит новое',
      'Видно, что клиент открыл документ, — не нужно спрашивать «посмотрели?»',
      'Собирается прямо в карточке сделки amoCRM, не выходя из CRM',
    ],
    note: 'Без карты · первая версия примерно через 15 минут',
    stepsTitle: 'Как составить КП онлайн',
    steps: [
      {
        num: 'ШАГ 01',
        title: 'Приложите материалы',
        text:
          'Бриф, прайс, техзадание, запись созвона с клиентом. Файлы агент читает сам, аудио ' +
          'расшифровывает по говорящим и вытаскивает задачи, сроки и цифры.',
      },
      {
        num: 'ШАГ 02',
        title: 'Уточните, что должно убеждать',
        text:
          'Агент спрашивает только недостающее: кто клиент, какие цифры важны, что нужно ' +
          'доказать. Не анкета на двадцать полей — вопросы по делу и по одному.',
      },
      {
        num: 'ШАГ 03',
        title: 'Проверьте смету и сроки',
        text:
          'Цифры видно сразу и целиком. Правки — словами в чате или руками прямо в превью: ' +
          'поправить строку сметы или дату можно самому, это бесплатно и не ждёт агента.',
      },
      {
        num: 'ШАГ 04',
        title: 'Отправьте и следите',
        text:
          'Ссылка показывает актуальную версию, вы видите, что клиент её открыл. Просят файл — ' +
          'PDF и PPTX собираются из того же документа. Ссылка отзывается в один клик.',
      },
    ],
    gainsTitle: 'Почему не шаблон из интернета',
    gains: [
      {
        title: 'Считает по вашему прайсу',
        text:
          'Скачанный образец придётся заполнять руками и пересчитывать. Здесь смета собирается ' +
          'из вашего прайса, а не из чужого примера.',
      },
      {
        title: 'Одна ссылка вместо «КП_финал_2»',
        text:
          'Версии живут внутри документа, а не в именах файлов. У клиента одна ссылка, и она ' +
          'всегда показывает последнее.',
      },
      {
        title: 'Правки без вёрстки',
        text:
          'Поправили цифру в готовом шаблоне — и вёрстка поехала. Здесь правка словами или ' +
          'руками в превью, оформление остаётся на месте.',
      },
      {
        title: 'Из карточки сделки',
        text:
          'Виджет ставится из амоМаркета: КП собирается прямо в сделке amoCRM или Kommo, без ' +
          'переключения между вкладками.',
      },
    ],
    templatesTitle: 'С чего начать',
    templatesNote:
      'Готовый шаблон коммерческого предложения — или чистый лист. Собранный проект ' +
      'сохраняется шаблоном организации, и следующее КП собирается уже по нему.',
    templates: ['Коммерческое предложение', 'Договор-оферта на услуги', 'Прайс-лист', 'Чистый лист'],
    faq: [
      {
        q: 'Как составить коммерческое предложение онлайн?',
        a:
          'Приложите материалы: бриф, прайс, запись созвона. Агент прочитает файлы, расшифрует ' +
          'аудио, задаст недостающие вопросы и соберёт КП со сметой и сроками. Первая версия ' +
          'готова примерно через 15 минут.',
      },
      {
        q: 'Можно сделать КП бесплатно?',
        a:
          'Новой организации документы для знакомства начисляются при регистрации, карта не ' +
          'нужна. Дальше подписка: она включает пакет документов на месяц, перерасход ' +
          'докупается по той же цене.',
      },
      {
        q: 'В каком виде клиент получит КП?',
        a:
          'Ссылкой, PDF или PPTX — на выбор, одной и той же кнопкой. Ссылка всегда показывает ' +
          'актуальную версию и отзывается в один клик; по ней же видно, что клиент открыл ' +
          'документ.',
      },
      {
        q: 'Есть ли интеграция с amoCRM?',
        a:
          'Да. Виджет ставится из амоМаркета, и КП собирается прямо в карточке сделки — не ' +
          'выходя из CRM. Kommo поддерживается так же.',
      },
      {
        q: 'Чем это отличается от шаблона в Word?',
        a:
          'Шаблон нужно заполнять руками и пересчитывать смету, а после правки цифр вёрстка ' +
          'обычно едет. Здесь смета собирается из вашего прайса, правки говорятся словами, а ' +
          'каждая версия сохраняется с возможностью отката.',
      },
    ],
  },
  {
    slug: 'landing',
    kind: 'landing',
    navLabel: 'Лендинги',
    stamp: 'ЛЕНДИНГ № 2026-007',
    ctaTitle: 'Следующий лендинг пусть соберёт агент',
    title: 'Создать лендинг нейросетью — сделать лендинг с ИИ | Keypi',
    description:
      'Создать лендинг: агент собирает одностраничный сайт из вашего описания и материалов. ' +
      '10 готовых шаблонов — от клиники и кофейни до SaaS и жилого комплекса. Форма заявки, ' +
      'свой домен, правки словами.',
    keywords:
      'создать лендинг, сделать лендинг, создать лендинг ии, нейросеть для лендинга, ' +
      'лендинг онлайн, конструктор лендингов, одностраничный сайт создать, лендинг под ключ',
    eyebrow: 'ЛЕНДИНГ ЗА ЧАС · 10 ШАБЛОНОВ · С ФОРМОЙ ЗАЯВКИ',
    h1: 'Создать лендинг — за час, без конструктора и верстальщика',
    lede:
      'Опишите, что продаёте, и приложите материалы — агент соберёт одностраничный сайт со ' +
      'структурой, текстами и формой заявки. Заявки приходят вам, а открыть страницу можно ' +
      'на своём домене.',
    bullets: [
      'Десять готовых шаблонов: продукт, SaaS, онлайн-школа, клиника, кофейня, спортзал и другие',
      'Форма заявки на странице — обращения приходят вам, отдельный сервис не нужен',
      'Правки словами: «поменяй блок с ценами местами», «добавь отзывы» — без вёрстки',
      'Свой домен для публичной ссылки — страница открывается под вашим именем',
    ],
    note: 'Без карты · первая версия примерно через 15 минут',
    stepsTitle: 'Как создать лендинг',
    steps: [
      {
        num: 'ШАГ 01',
        title: 'Выберите шаблон или начните с нуля',
        text:
          'Десять отраслевых заготовок — от кофейни и клиники до SaaS-сервиса и жилого ' +
          'комплекса. Если структура уже в голове, берите чистый лист.',
      },
      {
        num: 'ШАГ 02',
        title: 'Расскажите про предложение',
        text:
          'Что продаёте, кому, чем отличаетесь, какие цены. Материалы — прайс, фотографии, ' +
          'описание услуг, запись созвона — агент разберёт сам.',
      },
      {
        num: 'ШАГ 03',
        title: 'Правьте до результата',
        text:
          'Блоки, тексты и порядок — словами в чате. Мелочи быстрее поправить руками прямо в ' +
          'превью: это бесплатно и не ждёт агента. Каждая правка — версия с откатом.',
      },
      {
        num: 'ШАГ 04',
        title: 'Публикуйте и принимайте заявки',
        text:
          'Страница открывается по ссылке или на вашем домене. Форма заявки работает сразу — ' +
          'обращения приходят вам. Поправили после запуска — посетитель уже видит новое.',
      },
    ],
    gainsTitle: 'Почему не конструктор',
    gains: [
      {
        title: 'Не нужно собирать блоки руками',
        text:
          'В конструкторе структуру, тексты и порядок блоков придумываете вы. Здесь агент ' +
          'предлагает структуру сам — из того, что вы про предложение рассказали.',
      },
      {
        title: 'Правки словами, а не мышью',
        text:
          '«Перенеси цены выше, добавь блок с отзывами» — вместо перетаскивания секций и ' +
          'подгонки отступов на каждом экране.',
      },
      {
        title: 'Заявки уже внутри',
        text:
          'Форма на опубликованной странице работает сразу, отдельный сервис приёма заявок ' +
          'подключать не нужно.',
      },
      {
        title: 'Тот же агент делает и остальное',
        text:
          'КП, презентация и прайс собираются там же и в том же стиле — лендинг не остаётся ' +
          'единственным документом, который выглядит по-своему.',
      },
    ],
    templatesTitle: 'Готовые шаблоны лендингов',
    templatesNote:
      'Каждый — рабочая страница со структурой под свою отрасль, а не пустая рыба. Берётся ' +
      'как основа и дальше правится под вас.',
    templates: [
      'Лендинг продукта',
      'Лендинг SaaS-сервиса',
      'Лендинг онлайн-школы',
      'Лендинг клиники',
      'Лендинг кофейни',
      'Лендинг спортзала',
      'Лендинг жилого комплекса',
      'Лендинг студии',
      'Лендинг фермы',
      'Лендинг мясной фермы',
    ],
    faq: [
      {
        q: 'Как создать лендинг в Keypi?',
        a:
          'Выберите отраслевой шаблон или чистый лист, расскажите про предложение и приложите ' +
          'материалы. Агент соберёт страницу со структурой, текстами и формой заявки. Первая ' +
          'версия готова примерно через 15 минут.',
      },
      {
        q: 'Можно ли поставить свой домен?',
        a:
          'Да, публичная ссылка может открываться на вашем домене — страница будет под вашим ' +
          'именем. Настраивается это в кабинете владельцем организации.',
      },
      {
        q: 'Куда приходят заявки с лендинга?',
        a:
          'Форма заявки работает на опубликованной странице сразу, обращения приходят вам. ' +
          'Отдельный сервис приёма заявок подключать не нужно.',
      },
      {
        q: 'Чем это отличается от конструктора сайтов?',
        a:
          'В конструкторе структуру и тексты придумываете вы, а блоки собираете мышью. Здесь ' +
          'агент предлагает структуру сам и правит её словами. Мелочи при этом можно поправить ' +
          'руками прямо в превью.',
      },
      {
        q: 'Можно сделать лендинг бесплатно?',
        a:
          'Новой организации документы для знакомства начисляются при регистрации, карта не ' +
          'нужна. Дальше действует подписка с пакетом документов на месяц.',
      },
    ],
  },
];

export function clusterBySlug(slug: string): Cluster | undefined {
  return CLUSTERS.find((cluster) => cluster.slug === slug);
}
